import { ActionModal } from '@kcftech/design-system';
import {
    IAssetCategoryParameterResponse,
    IAssetCategoryResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { useMutation } from '@tanstack/react-query';
import { useNotificationContext } from 'contexts/NotificationContext';
import { AssetParameterFormContent } from 'pages/Settings/AssetType/parameter_form/AssetParameterFormContent';
import { buildCategoryParameter } from 'pages/Settings/AssetType/parameter_form/shared';
import { AssetParameterFormData } from 'pages/Settings/AssetType/parameter_form/types';
import { useForm } from 'react-hook-form';
import { updateAssetParameter } from 'utils/asset_classification';

export type AssetParameterEditInfo = Readonly<{
    parameter: IAssetCategoryParameterResponse;
    siblingParameters: IAssetCategoryParameterResponse[];
    category: IAssetCategoryResponse;
}>;

export type EditAssetParameterProps = AssetParameterEditInfo &
    Readonly<{
        onClose: (saved: boolean) => void;
    }>;

export function EditAssetParameterModal({
    parameter,
    siblingParameters,
    category,
    onClose,
}: EditAssetParameterProps) {
    const { showToast } = useNotificationContext();

    const form = useForm<AssetParameterFormData>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        shouldUnregister: true,
        defaultValues: {
            name: parameter.Name,
            code: parameter.Code,
            type: parameter.CategoryParameterType,
            minValue: parameter.MinValue !== undefined ? `${parameter.MinValue}` : null,
            maxValue: parameter.MaxValue !== undefined ? `${parameter.MaxValue}` : null,
            minValueInclusive: parameter.MinValueInclusive,
            maxValueInclusive: parameter.MaxValueInclusive,
            defaultValue: parameter.DefaultValue ?? '',
            validValues: parameter.ValidValues,
        },
    });

    const {
        formState: { isSubmitted, isValid, isValidating, isSubmitting },
        handleSubmit,
    } = form;

    const { mutate: updateParameter, isPending: isUpdating } = useMutation({
        mutationFn: async (formData: AssetParameterFormData) =>
            updateAssetParameter(
                parameter.Category.AssetCategoryId,
                parameter.Code,
                buildCategoryParameter(formData)
            ),

        onSuccess: () => {
            onClose(true);
            showToast({
                type: 'success',
                content: 'Successfully updated asset category parameter',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to update asset category parameter',
            }),
    });

    const onUpdate = handleSubmit((formData) => updateParameter(formData));

    return (
        <ActionModal
            title="Edit Asset Category Parameter"
            actionable={
                (!isSubmitted || isValid) && !(isValidating || isSubmitting || isUpdating)
            }
            onAction={onUpdate}
            onClose={() => onClose(false)}
            actionButtonText="Save"
            closeButtonText="Cancel"
        >
            <AssetParameterFormContent
                form={form}
                siblingParameters={siblingParameters}
                parameter={parameter}
                parentCategory={category}
            />
        </ActionModal>
    );
}
